<script lang="ts">
    import { onDestroy } from 'svelte';
    import { get } from 'svelte/store';
    import { push } from 'svelte-spa-router';
    import { currentUser, authReady } from '$lib/stores/auth';
    import {
        adminListBallotImports,
        adminUploadBallotFile,
        adminBallotSyncNow,
        humanizeError,
        ApiError,
        type BallotImport,
    } from '$lib/api';
    import AdminNav from '../components/AdminNav.svelte';

    // A prop, not a constant, so a test can poll fast without fake timers.
    export let pollMs = 3000;

    let imports: BallotImport[] = [];
    let loading = true;
    let error: string | null = null;

    let fileInput: HTMLInputElement | null = null;
    let uploading = false;
    let uploadMessage: string | null = null;
    let uploadError: string | null = null;

    let pulling = false;
    let pullMessage: string | null = null;
    let pullError: string | null = null;

    let pollTimer: ReturnType<typeof setInterval> | null = null;
    let destroyed = false;
    // Same poll discipline as AdminDeliveries: stop after 3 failures in a row
    // or at once on a 401; Retry restarts it.
    let consecutiveFailures = 0;
    let pollSuspended = false;

    async function load() {
        if (!get(authReady)) return;
        if (!$currentUser || $currentUser.role !== 'admin') {
            push('/map');
            return;
        }
        try {
            const resp = await adminListBallotImports();
            if (destroyed) return;
            imports = resp.items;
            error = null;
            consecutiveFailures = 0;
            pollSuspended = false;
        } catch (e: any) {
            if (destroyed) return;
            error = humanizeError(e, 'Failed to load ballot imports');
            consecutiveFailures += 1;
            const sessionExpired = e instanceof ApiError && e.status === 401;
            if (sessionExpired || consecutiveFailures >= 3) {
                pollSuspended = true;
                if (pollTimer !== null) {
                    clearInterval(pollTimer);
                    pollTimer = null;
                }
            }
        } finally {
            if (!destroyed) loading = false;
        }
        syncPolling();
    }

    function retry() {
        consecutiveFailures = 0;
        pollSuspended = false;
        load();
    }

    function syncPolling() {
        if (destroyed || pollSuspended) {
            if (pollTimer !== null) {
                clearInterval(pollTimer);
                pollTimer = null;
            }
            return;
        }
        const anyRunning = imports.some((d) => d.status === 'running');
        if (anyRunning && pollTimer === null) {
            pollTimer = setInterval(load, pollMs);
        } else if (!anyRunning && pollTimer !== null) {
            clearInterval(pollTimer);
            pollTimer = null;
        }
    }

    async function upload() {
        if (uploading) return;
        const file = fileInput?.files?.[0];
        if (!file) {
            uploadMessage = null;
            uploadError = 'Choose a .csv or .xlsx file first.';
            return;
        }
        uploading = true;
        uploadError = null;
        uploadMessage = null;
        try {
            const result = await adminUploadBallotFile(file);
            uploadMessage = result.status === 'unchanged'
                ? 'That file is already the latest import; nothing changed.'
                : 'Import started';
            if (fileInput) fileInput.value = '';
            await load();
        } catch (e: any) {
            uploadError = humanizeError(e, 'Upload failed');
        } finally {
            uploading = false;
        }
    }

    async function pullNow() {
        if (pulling) return;
        pulling = true;
        pullError = null;
        pullMessage = null;
        try {
            const r = await adminBallotSyncNow();
            if (r.status === 'ok') {
                pullMessage = `Pulled ${fmtNum(r.rows ?? 0)} rows: matched ${fmtNum(r.matched ?? 0)}, voted ${fmtNum(r.voted ?? 0)}`;
            } else if (r.status === 'unchanged') {
                pullMessage = 'NCSBE has nothing new since the last import.';
            } else {
                pullError = `Pull failed${r.error ? ` (${r.error})` : ''}. See the table below.`;
            }
            await load();
        } catch (e: any) {
            pullError = humanizeError(e, 'Pull failed');
        } finally {
            pulling = false;
        }
    }

    const unsubUser = currentUser.subscribe(() => { load(); });
    const unsubReady = authReady.subscribe(() => { load(); });
    onDestroy(() => {
        destroyed = true;
        unsubUser();
        unsubReady();
        if (pollTimer !== null) clearInterval(pollTimer);
    });

    function fmtTime(s: string | null): string {
        if (!s) return 'none';
        return new Date(s).toLocaleString([], {
            year: 'numeric', month: 'short', day: 'numeric',
            hour: 'numeric', minute: '2-digit',
        });
    }

    function fmtNum(n: number): string {
        return n.toLocaleString();
    }

    const STATUS_BADGE: Record<string, string> = {
        ok: 'badge-ok',
        error: 'badge-error',
        running: 'badge-warn',
    };
</script>

<div class="admin-page">
    <header class="admin-header">
        <span class="eyebrow">Admin, early vote</span>
        <h1>Early vote</h1>
        <p class="page-sub">NCSBE absentee and one-stop files: the twice-daily pull, manual uploads, and what each import marked.</p>
    </header>

    <AdminNav active="ballots" />

    <section class="block">
        <div class="block-head"><h2>Add a ballot file</h2></div>
        <div class="upload-row">
            <label class="file-label" for="ballot-file">Ballot file</label>
            <input
                id="ballot-file"
                type="file"
                accept=".csv,.xlsx"
                bind:this={fileInput}
                on:change={() => { uploadMessage = null; uploadError = null; }}
            />
            <button class="btn btn-primary" on:click={upload} disabled={uploading}>
                {uploading ? 'Uploading' : 'Upload'}
            </button>
        </div>
        {#if uploadMessage}<p class="msg msg-ok">{uploadMessage}</p>{/if}
        {#if uploadError}<p class="msg msg-error">{uploadError}</p>{/if}

        <div class="upload-row">
            <button class="btn" on:click={pullNow} disabled={pulling}>
                {pulling ? 'Pulling' : 'Pull from NCSBE now'}
            </button>
        </div>
        <p class="muted pull-hint">A pull downloads the statewide file and can take a minute or two. Do not click again.</p>
        {#if pullMessage}<p class="msg msg-ok">{pullMessage}</p>{/if}
        {#if pullError}<p class="msg msg-error">{pullError}</p>{/if}
    </section>

    <section class="block">
        <div class="block-head"><h2>Recent imports</h2></div>
        {#if error}
            <p class="msg msg-error banner-row">
                <span>{error}</span>
                <button class="btn" on:click={retry}>Retry</button>
            </p>
        {/if}
        {#if loading}
            <p class="loading">Loading</p>
        {:else if imports.length === 0}
            <p class="muted">No ballot files imported yet.</p>
        {:else}
            <div class="table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th>Started</th>
                            <th>Source</th>
                            <th>File</th>
                            <th>Status</th>
                            <th class="num">Rows</th>
                            <th class="num">Matched</th>
                            <th class="num">Voted</th>
                            <th>Error</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each imports as d (d.id)}
                            <tr>
                                <td class="mono">{fmtTime(d.started_at)}</td>
                                <td>{d.source}</td>
                                <td class="file-cell">{d.filename}</td>
                                <td><span class="badge {STATUS_BADGE[d.status] ?? ''}">{d.status}</span></td>
                                <td class="num mono">{fmtNum(d.row_count)}</td>
                                <td class="num mono">{fmtNum(d.matched_count)}</td>
                                <td class="num mono">{fmtNum(d.voted_count)}</td>
                                <td class="error-cell">{d.error_message ?? ''}</td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        {/if}
    </section>
</div>

<style>
    .admin-page {
        max-width: 1080px;
        margin: 0 auto;
        padding: var(--s-7) var(--s-5) var(--s-8);
        color: var(--ink);
    }
    .admin-header { margin-bottom: var(--s-5); }
    .admin-header .eyebrow {
        color: var(--leaf-bright);
        display: block;
        margin-bottom: var(--s-2);
    }
    .admin-header h1 {
        font-family: var(--font-display);
        font-size: clamp(2rem, 5vw, 2.8rem);
        margin: 0 0 var(--s-2);
        font-weight: 600;
    }
    .page-sub { color: var(--ink-soft); font-size: 1rem; margin: 0; }

    .block { margin-top: var(--s-7); }
    .block-head {
        display: flex;
        align-items: baseline;
        justify-content: space-between;
        gap: var(--s-3);
        margin-bottom: var(--s-4);
        padding-bottom: var(--s-3);
        border-bottom: 1px solid var(--rule);
    }
    .block-head h2 {
        font-family: var(--font-display);
        font-size: 1.3rem;
        font-weight: 500;
        margin: 0;
    }

    .upload-row {
        display: flex;
        align-items: center;
        gap: var(--s-3);
        flex-wrap: wrap;
        margin-bottom: var(--s-3);
    }
    .file-label {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--ink-muted);
        font-weight: 600;
    }

    .table-wrap {
        background: var(--field);
        border: 1px solid var(--rule);
        overflow-x: auto;
    }
    table { width: 100%; border-collapse: collapse; font-size: 0.9rem; }
    th, td { text-align: left; padding: 12px 14px; border-bottom: 1px solid var(--rule); }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover { background: var(--paper-soft); }
    th {
        font-size: 0.72rem;
        text-transform: uppercase;
        letter-spacing: 0.1em;
        color: var(--ink-muted);
        font-weight: 600;
        background: var(--paper);
        border-bottom: 2px solid var(--rule);
    }
    th.num, td.num { text-align: right; }
    .mono { font-family: var(--font-mono); }
    .file-cell { max-width: 260px; word-break: break-all; }

    .badge {
        display: inline-block;
        padding: 3px 10px;
        font-size: 0.72rem;
        font-weight: 600;
        letter-spacing: 0.04em;
        text-transform: lowercase;
        border: 1px solid;
    }
    .badge-ok    { background: var(--leaf-soft);  color: var(--leaf-ink); border-color: var(--leaf-bright); }
    .badge-error { background: var(--alert-soft); color: var(--alert);    border-color: var(--alert); }
    .badge-warn  { background: var(--sun-soft);   color: var(--ink);      border-color: var(--sun); }

    .error-cell { color: var(--alert); max-width: 240px; word-break: break-word; font-size: .78rem; }

    .btn {
        font-family: var(--font-body);
        font-size: 0.85rem;
        font-weight: 600;
        padding: 8px 16px;
        cursor: pointer;
        border: 1px solid var(--ink);
        background: var(--paper);
        color: var(--ink);
        transition: background 0.15s, color 0.15s;
    }
    .btn-primary { background: var(--ink); color: var(--paper); }
    .btn:hover:not(:disabled) { background: var(--leaf); border-color: var(--leaf); color: var(--paper); }
    .btn:disabled { opacity: 0.5; cursor: not-allowed; }

    .msg {
        margin: 0 0 var(--s-3);
        padding: 10px 14px;
        font-size: 0.9rem;
        border-left: 3px solid;
    }
    .msg-ok    { background: var(--leaf-soft);  border-color: var(--leaf-bright); color: var(--leaf-ink); }
    .msg-error { background: var(--alert-soft); border-color: var(--alert);       color: var(--alert); }

    .muted      { color: var(--ink-muted); font-style: italic; font-size: 0.9rem; }
    .pull-hint { margin: 0 0 var(--s-3); }
    .loading    { color: var(--ink-soft); font-size: 0.95rem; padding: var(--s-5) 0; }

    .banner-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: var(--s-3);
    }
</style>
